/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import type { CSSProperties } from 'react';
import { Text, View } from '@az/starc-ui';
import NextImage from '@/components/NextImage';
import { SmartLink } from '@/utils/smartLink';
import styles from './FeatureCard.module.scss';

export type FeatureCardBackground =
  | { type: 'color'; value: string }
  | { type: 'image'; src: string; alt?: string };

export type FeatureCardImage = {
  src: string;
  alt: string;
  width: number;
  height: number;
};

export type FeatureCardCta = {
  href: string;
};

export type FeatureCardProps = {
  title: string;
  subtitle: string;
  image?: FeatureCardImage;
  cta: FeatureCardCta;
  background: FeatureCardBackground;
  textColor?: string;
};

const FeatureCard = ({ title, subtitle, image, cta, background, textColor }: FeatureCardProps) => {
  const backgroundStyle: CSSProperties =
    background.type === 'color'
      ? { backgroundColor: background.value, color: textColor }
      : { backgroundImage: `url(${background.src})`, color: textColor };

  return (
    <SmartLink
      to={cta.href}
      className={styles.FeatureCard}
      style={backgroundStyle}
      aria-label={`${title} ${subtitle}`}
    >
      <View className={styles.Content}>
        <Text as="h3" fontFamily="body" weight="bold" className={styles.Title}>
          {title}
        </Text>
        <Text as="p" fontFamily="body" weight="regular" className={styles.Subtitle}>
          {subtitle}
        </Text>
      </View>

      <View className={styles.Spacer} />

      {image && (
        <View className={styles.ImageWrapper}>
          <NextImage
            src={image.src}
            alt={image.alt}
            width={image.width}
            height={image.height}
            className={styles.Image}
          />
        </View>
      )}
    </SmartLink>
  );
};

export default FeatureCard;
